/**
 * UnifiedThresholdDialog - Manage shop thresholds for a product
 * Shows all shops in one dialog for bulk editing
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, Sliders } from "lucide-react";
import { SHOP_TYPE_STYLES, THRESHOLD_SOURCE_STYLES, THRESHOLD_SOURCE_LABELS } from "./constants";

export default function UnifiedThresholdDialog({
  open,
  onOpenChange,
  product,
  data,
  loading,
  saving,
  edits,
  onEdit,
  onClear,
  onUndo,
  onSave,
}) {
  const hasEdits = Object.keys(edits).length > 0;

  const handleOpenChange = (newOpen) => {
    if (!newOpen && hasEdits) {
      if (!confirm("You have unsaved changes. Discard them?")) return;
    }
    onOpenChange(newOpen);
  };

  const handleCancel = () => {
    if (hasEdits && !confirm("Discard unsaved changes?")) return;
    onOpenChange(false);
  };

  // Calculate fallback values when clearing an override
  const getFallbackValues = (shop) => {
    if (!data) return { source: "default", min: 0, max: 0 };
    
    const productThreshold = data.product_threshold;
    const defaultSettings = data.default_settings;
    
    if (shop.shop_type === "warehouse" && productThreshold?.has_warehouse_custom) {
      return {
        source: "custom",
        min: productThreshold.warehouse_min_qty,
        max: productThreshold.warehouse_max_qty,
      };
    } else if (shop.shop_type !== "warehouse" && productThreshold?.has_normal_custom) {
      return {
        source: "custom",
        min: productThreshold.normal_min_qty,
        max: productThreshold.normal_max_qty,
      };
    } else {
      const def = shop.shop_type === "warehouse" ? defaultSettings?.warehouse : defaultSettings?.normal;
      return {
        source: "default",
        min: def?.min_qty ?? 0,
        max: def?.max_qty ?? 0,
      };
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="unified-threshold-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-purple-600" />
            Manage Shop Thresholds
          </DialogTitle>
          <DialogDescription>
            Configure threshold overrides for all shops in one place. Only modified values will be saved.
          </DialogDescription>
        </DialogHeader>

        {/* Product Info */}
        {product && (
          <div className="bg-muted p-3 rounded">
            <div className="font-medium">{product.product_name}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {product.brand_name} · {product.group_name}
            </div>
          </div>
        )}

        {/* Priority Explanation */}
        <div className="text-xs text-muted-foreground bg-gradient-to-r from-purple-50 to-amber-50 p-2 rounded border border-purple-200">
          <strong>Threshold Priority:</strong> Shop Override (highest) → Product Custom → Shop-Type Default (lowest)
        </div>

        {/* Scrollable Shop List */}
        <div className="flex-1 overflow-y-auto min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Loading shop thresholds...
            </div>
          ) : data ? (
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead className="w-[140px]">Shop</TableHead>
                  <TableHead className="w-[80px] text-center">Type</TableHead>
                  <TableHead className="w-[90px] text-center">Effective</TableHead>
                  <TableHead className="w-[80px] text-center">Source</TableHead>
                  <TableHead className="w-[90px] text-center">Override Min</TableHead>
                  <TableHead className="w-[90px] text-center">Override Max</TableHead>
                  <TableHead className="w-[70px] text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.shops.map(shop => {
                  const edit = edits[shop.shop_id] || {};
                  const isClearing = edit.clear === true;
                  const isEdited = edit.min_qty !== undefined || edit.max_qty !== undefined;
                  const hasOverride = shop.has_shop_override;
                  
                  // Determine effective display values
                  let displaySource = shop.source;
                  let displayMin = shop.min_qty;
                  let displayMax = shop.max_qty;
                  
                  if (isClearing) {
                    const fallback = getFallbackValues(shop);
                    displaySource = fallback.source;
                    displayMin = fallback.min;
                    displayMax = fallback.max;
                  }
                  
                  return (
                    <TableRow 
                      key={shop.shop_id} 
                      className={`${isClearing ? "bg-red-50/50" : isEdited ? "bg-purple-50/50" : ""}`}
                      data-testid={`unified-shop-row-${shop.shop_id}`}
                    >
                      <TableCell className="font-medium">{shop.shop_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-[9px] ${SHOP_TYPE_STYLES[shop.shop_type || "normal"]}`}>
                          {shop.shop_type === "warehouse" ? "WH" : "NOR"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-mono text-sm ${isClearing ? "line-through text-gray-400" : ""}`}>
                          {displayMin}-{displayMax}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-[8px] ${THRESHOLD_SOURCE_STYLES[displaySource]}`}>
                          {THRESHOLD_SOURCE_LABELS[displaySource]}
                        </Badge>
                        {isClearing && (
                          <span className="block text-[9px] text-red-600 mt-0.5">→ CLEARING</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isClearing ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            placeholder={String(shop.override_min_qty ?? "")}
                            value={edit.min_qty ?? ""}
                            onChange={(e) => onEdit(shop.shop_id, "min_qty", e.target.value)}
                            className="h-7 text-center text-sm w-16 mx-auto"
                            data-testid={`unified-min-${shop.shop_id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isClearing ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            placeholder={String(shop.override_max_qty ?? "")}
                            value={edit.max_qty ?? ""}
                            onChange={(e) => onEdit(shop.shop_id, "max_qty", e.target.value)}
                            className="h-7 text-center text-sm w-16 mx-auto"
                            data-testid={`unified-max-${shop.shop_id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isClearing || isEdited ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onUndo(shop.shop_id)}
                            className="h-7 px-2 text-xs"
                            data-testid={`unified-undo-${shop.shop_id}`}
                          >
                            Undo
                          </Button>
                        ) : hasOverride ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onClear(shop.shop_id)}
                            className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`unified-clear-${shop.shop_id}`}
                          >
                            Clear
                          </Button>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}
        </div>

        {/* Summary of Changes */}
        {hasEdits && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
            <strong className="text-amber-800">Pending Changes:</strong>
            <span className="ml-2 text-amber-700">
              {Object.entries(edits).filter(([_, e]) => e.min_qty !== undefined || e.max_qty !== undefined).length} edits,{" "}
              {Object.entries(edits).filter(([_, e]) => e.clear).length} clears
            </span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !hasEdits}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-unified-thresholds-btn"
          >
            {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
